/**
 * Descrittori dei campi degli schemi ANAC, usati per generare il form
 * del passo 2.
 *
 * La fonte autorevole e' `GET /api/v1/schemi` (CONVENTIONS §5), che pero' e'
 * riservato al super admin: un redattore non puo' interrogarlo. Questo modulo
 * tiene percio' un **descrittore di riserva**, con la stessa forma di quello che
 * l'API restituisce, in modo che il back-office sappia comunque quali campi chiedere
 * e con quale maschera italiana.
 *
 * Regola d'uso: se l'API espone `campi` per uno schema, vincono quelli; questo
 * catalogo interviene solo quando mancano. I valori restano **stringhe gia' in
 * formato italiano** e vengono inviati tali e quali in `dati_strutturati`.
 *
 * Riferimenti: delibera ANAC 495/2024 come modificata dalla 481/2025 (Allegati
 * 1, 2 e 3, obbligatori dal 22/01/2026) e delibera 497/2025 per gli schemi
 * ulteriori. Prima del rilascio i campi vanno riconciliati con le specifiche
 * tecniche pubblicate: qui rappresentano i soli elementi comuni a tutti i
 * tracciati.
 *
 * TRE COPIE, UN SOLO TRACCIATO
 * ----------------------------
 * Lo stesso elenco di campi vive in tre punti del sistema, perche' i tre
 * servizi non si importano a vicenda:
 *
 * * `api/app/schemas/anac/allegato_{1,2,3}.py` — la validazione in ingresso;
 * * `portale/schemi_anac.py` — le intestazioni della tabella pubblicata;
 * * questo file — il modulo che il redattore compila.
 *
 * Una copia **incompleta qui** e' peggio di una copia disallineata altrove: il
 * la redazione non chiama `GET /api/v1/schemi` per chi non e' super admin, quindi
 * questi descrittori sono l'unica strada del redattore. Un campo che manca qui
 * e' un campo che nessuno puo' inserire, mentre il portale continua a
 * pubblicarne la colonna vuota. E' successo con `norma_riferimento`, `pec` e
 * `provvedimenti_adottati`, aggiunti dopo la seconda tornata di verifica.
 *
 * L'allineamento delle tre copie — nomi **e ordine**, che e' parte del
 * tracciato — e' ora asserito da `verifiche/schemi.verifica.ts`.
 */

import type { CampoSchema, SchemaAnac } from "../api/types";

/**
 * Tipologie di beneficiario ammesse dallo schema dell'art. 4.
 *
 * La delibera 481/2025 ha **rimosso il codice fiscale del beneficiario** e lo ha
 * sostituito con questa opzione vincolata: e' minimizzazione, non una scelta di
 * interfaccia, e per questo il campo qui non esiste proprio.
 */
export const TIPOLOGIE_BENEFICIARIO = [
  "Persona fisica",
  "Impresa",
  "Ente pubblico",
  "Ente del terzo settore",
  "Altro soggetto giuridico",
] as const;

/** Campi comuni a tutti gli schemi: identificano il periodo di riferimento. */
const CAMPI_PERIODO: CampoSchema[] = [
  {
    nome: "anno",
    etichetta: "Anno di riferimento",
    tipo: "anno",
    obbligatorio: true,
    aiuto: "Quattro cifre, per esempio 2026.",
  },
];

const CAMPI_ALLEGATO_1: CampoSchema[] = [
  ...CAMPI_PERIODO,
  {
    nome: "trimestre",
    etichetta: "Trimestre",
    tipo: "trimestre",
    obbligatorio: false,
    aiuto: "Un numero da 1 a 4, se il dato e' trimestrale.",
  },
  {
    nome: "tipologia_beneficiario",
    etichetta: "Tipologia di beneficiario",
    tipo: "opzioni",
    obbligatorio: true,
    opzioni: TIPOLOGIE_BENEFICIARIO,
    aiuto: "Opzione vincolata. Il codice fiscale del beneficiario non va pubblicato (delibera 481/2025).",
  },
  {
    nome: "denominazione_beneficiario",
    etichetta: "Denominazione del beneficiario",
    tipo: "testo",
    obbligatorio: true,
  },
  {
    nome: "importo",
    etichetta: "Importo",
    tipo: "importo",
    obbligatorio: true,
    aiuto: "Formato italiano: punto per le migliaia, virgola per i decimali.",
  },
  {
    nome: "data_pagamento",
    etichetta: "Data del pagamento",
    tipo: "data",
    obbligatorio: true,
  },
  {
    nome: "causale",
    etichetta: "Causale",
    tipo: "testo_lungo",
    obbligatorio: true,
  },
  {
    nome: "ufficio_responsabile",
    etichetta: "Ufficio responsabile",
    tipo: "testo",
    obbligatorio: false,
  },
  {
    nome: "norma_riferimento",
    etichetta: "Norma di riferimento",
    tipo: "testo",
    obbligatorio: false,
    aiuto: "Norma o titolo giuridico su cui si fonda l'erogazione.",
  },
];

const CAMPI_ALLEGATO_2: CampoSchema[] = [
  ...CAMPI_PERIODO,
  {
    nome: "denominazione_articolazione",
    etichetta: "Denominazione dell'articolazione",
    tipo: "testo",
    obbligatorio: true,
  },
  {
    nome: "competenze",
    etichetta: "Competenze",
    tipo: "testo_lungo",
    obbligatorio: true,
  },
  {
    nome: "responsabile",
    etichetta: "Responsabile",
    tipo: "testo",
    obbligatorio: true,
  },
  {
    nome: "telefono",
    etichetta: "Telefono",
    tipo: "testo",
    obbligatorio: false,
  },
  {
    nome: "email_istituzionale",
    etichetta: "Posta elettronica istituzionale",
    tipo: "email",
    obbligatorio: true,
  },
  {
    nome: "pec",
    etichetta: "Posta elettronica certificata",
    tipo: "email",
    obbligatorio: false,
    aiuto: "Casella PEC dell'ufficio. E' un recapito dell'articolazione, non della persona.",
  },
  {
    nome: "url_organigramma",
    etichetta: "Indirizzo dell'organigramma",
    tipo: "url",
    obbligatorio: false,
    aiuto: "URL completo, per esempio https://comune.esempio.to.it/organigramma.",
  },
];

const CAMPI_ALLEGATO_3: CampoSchema[] = [
  ...CAMPI_PERIODO,
  {
    nome: "organo_controllo",
    etichetta: "Organo di controllo",
    tipo: "testo",
    obbligatorio: true,
  },
  {
    nome: "tipologia_atto",
    etichetta: "Tipologia dell'atto",
    tipo: "testo",
    obbligatorio: true,
  },
  {
    nome: "data_atto",
    etichetta: "Data dell'atto",
    tipo: "data",
    obbligatorio: true,
  },
  {
    nome: "oggetto",
    etichetta: "Oggetto",
    tipo: "testo_lungo",
    obbligatorio: true,
  },
  {
    nome: "rilievi_percentuale",
    etichetta: "Quota di rilievi accolti",
    tipo: "percentuale",
    obbligatorio: false,
    aiuto: "Percentuale senza simbolo, per esempio 75,33.",
  },
  {
    nome: "provvedimenti_adottati",
    etichetta: "Provvedimenti adottati",
    tipo: "testo_lungo",
    obbligatorio: false,
    aiuto: "Provvedimenti adottati a seguito dei rilievi, se previsti.",
  },
  {
    nome: "url_documento",
    etichetta: "Indirizzo del documento",
    tipo: "url",
    obbligatorio: false,
  },
];

/**
 * Schemi noti al back-office in assenza di risposta da `GET /api/v1/schemi`.
 * Le date di decorrenza sono gia' in formato italiano, come ovunque nel sistema.
 */
export const SCHEMI_DI_RISERVA: readonly SchemaAnac[] = [
  {
    id_schema: "allegato_1_art_4_bis",
    denominazione: "Allegato 1 — Trasparenza nell'utilizzo delle risorse pubbliche",
    riferimento_normativo: "d.lgs. 33/2013, art. 4-bis",
    delibera: "ANAC 495/2024, come modificata dalla 481/2025",
    decorrenza: "22/01/2026",
    attivo: true,
    campi: CAMPI_ALLEGATO_1,
  },
  {
    id_schema: "allegato_2_art_13",
    denominazione: "Allegato 2 — Organizzazione delle pubbliche amministrazioni",
    riferimento_normativo: "d.lgs. 33/2013, art. 13",
    delibera: "ANAC 495/2024, come modificata dalla 481/2025",
    decorrenza: "22/01/2026",
    attivo: true,
    campi: CAMPI_ALLEGATO_2,
  },
  {
    id_schema: "allegato_3_art_31",
    denominazione: "Allegato 3 — Controlli sull'organizzazione e sull'attivita'",
    riferimento_normativo: "d.lgs. 33/2013, art. 31",
    delibera: "ANAC 495/2024, come modificata dalla 481/2025",
    decorrenza: "22/01/2026",
    attivo: true,
    campi: CAMPI_ALLEGATO_3,
  },
];

/**
 * Schemi ulteriori con specifiche tecniche gia' disponibili (delibera 497/2025).
 * Non hanno ancora descrittori di campo: si dichiarano per completezza del
 * pannello «Schemi ANAC» e restano non compilabili nel back-office.
 */
export const SCHEMI_ULTERIORI: readonly SchemaAnac[] = (
  [
    ["art_12", "art. 12", "Atti normativi e amministrativi generali"],
    ["art_20", "art. 20", "Dati relativi ai premi"],
    ["art_29", "art. 29", "Bilanci"],
    ["art_35", "art. 35", "Attivita' e procedimenti"],
    ["art_36", "art. 36", "Requisiti di accesso ai procedimenti"],
  ] as const
).map(([suffisso, articolo, oggetto]) => ({
  id_schema: `schema_${suffisso}`,
  denominazione: `${oggetto} (${articolo})`,
  riferimento_normativo: `d.lgs. 33/2013, ${articolo}`,
  delibera: "ANAC 497/2025",
  decorrenza: "22/01/2026",
  attivo: false,
  campi: undefined,
}));

/** Schema fra quelli noti, per identificativo. */
export function schemaDiRiserva(idSchema: string): SchemaAnac | undefined {
  return [...SCHEMI_DI_RISERVA, ...SCHEMI_ULTERIORI].find(
    (schema) => schema.id_schema === idSchema,
  );
}

/**
 * Campi da mostrare per uno schema: quelli dell'API se presenti, altrimenti
 * quelli di riserva. Vuoto se lo schema non ha ancora un tracciato definito.
 */
export function campiDelloSchema(schema: SchemaAnac | undefined): readonly CampoSchema[] {
  if (!schema) return [];
  if (schema.campi && schema.campi.length > 0) return schema.campi;
  return schemaDiRiserva(schema.id_schema)?.campi ?? [];
}
